import { Baseline } from '@shared/models/project.model';
import { IProposal } from '@shared/models/proposals.model';
import { applyProjectChangesToParent } from '@shared/helpers/pipeline/update-engine.helpers';
import { condenseChanges, type ConflictInfo } from './condense-changes.helpers';
import { dedupeRedundantResolvedWhenOverridePresentWithinAction } from './clean-actions.helpers';
import {
  sortProposalChangesWithRecipeFirst,
  splitProposalStructureChanges,
} from './proposal-change-order.helpers';
import type { ProjectChange } from '@shared/helpers/pipeline/update-engine.models';
import { ItemOrItemId } from '@shared/models/type-helpers.model';
import { getProposal } from '../proposals/proposals.helpers';

export type ResolveResult = {
  version: Baseline;
  conflicts: ConflictInfo[];
};

/**
 * Resolve a baseline version with a proposal's actions applied.
 * Collects all changes from referenced actions, condenses them via {@link condenseChanges},
 * and applies via {@link applyProjectChangesToParent}.
 *
 * Invalid graphs are rejected by `isValidProposalsAndActions` (used from `validateProject` in
 * project-validation): unknown proposal action ids, actions not referenced by any proposal, empty actions, etc.
 * This helper only applies changes for actions that exist on {@link version.actions}.
 *
 * @param version - Baseline version (unmodified)
 * @param proposalOrId - Proposal object or id whose {@link IProposal.actionIds} reference {@link Baseline.actions}
 * @returns Resolved version with changes applied plus any conflicts detected during condensation
 */
export const resolveVersionForProposal = (
  version: Baseline,
  proposalOrId: ItemOrItemId<IProposal> | undefined,
): ResolveResult => {
  const proposal = getProposal(version, proposalOrId);
  const actions = version.actions;

  if (!proposal || !actions?.length) return { version, conflicts: [] };

  const actionIdSet = new Set(proposal.actionIds);
  const relevantActions = actions.filter((a) => actionIdSet.has(a.id));

  // Build flat changes plus boundary indices so condenseChanges does not absorb a later
  // action's property edit into an earlier action's ElementActivate (would silently revert
  // post-duplicate quantity edits, see DEV-3265 follow-up).
  const allChanges: ProjectChange[] = [];
  const actionBoundaryIndices: number[] = [];
  for (const action of relevantActions) {
    if (allChanges.length > 0) actionBoundaryIndices.push(allChanges.length);
    // Inactive changes reference retroactively-dead positional ids and must never
    // apply again — positional ids are reused, so the id may now belong to a
    // different element (DEV-3640).
    const activeChanges = action.changes.filter((change) => !change.inactive);
    for (const change of sortProposalChangesWithRecipeFirst(
      dedupeRedundantResolvedWhenOverridePresentWithinAction(activeChanges),
    )) {
      allChanges.push(change);
    }
  }

  if (allChanges.length === 0) {
    return { version, conflicts: [] };
  }

  const { changes: condensed, conflicts } = condenseChanges(allChanges, {
    actionBoundaryIndices,
    version,
  });

  if (condensed.length === 0) {
    return { version, conflicts };
  }

  const { structureChanges, followUpChanges } =
    splitProposalStructureChanges(condensed);

  // Recipe children get new ids; apply structure changes one at a time so a parent
  // {@link ProjectChangeType.ElementRecipeApply} materializes proposal-only children
  // before a nested recipe apply on those ids (DEV-3293).
  let resolved = version;
  for (const change of structureChanges) {
    resolved = applyProjectChangesToParent(resolved, [change]);
  }
  if (followUpChanges.length > 0) {
    resolved = applyProjectChangesToParent(resolved, followUpChanges);
  }

  return { version: resolved, conflicts };
};
